import { useContext, useEffect, useState } from 'react';
import { Form, Input, Select, Switch, Button, notification, message, Space } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
const { Option } = Select;
import { ApiContext } from 'utils/networking/Api.context';
import { AppContext } from '../../../App.context';
import styles from './SalesChannels.styles';

export function HBXChannelForm({ channelFields, hotelData, selectOptions }) {
  if (!channelFields?.fields?.length) return null;

  const [finalUrl, setFinalUrl] = useState('');
  const [form] = Form.useForm();
  const { API } = useContext(ApiContext);
  const { fields, channel } = channelFields;
  const { hotelId, hotelSlug } = hotelData;
  const {
    appActions: { translate },
  } = useContext(AppContext);

  useEffect(() => {
    if (fields?.length) {
      const newValues = fields.reduce((acc, field) => {
        if (field.fieldName === 'hotel_slug' && !field.fieldValue) field.fieldValue = hotelSlug;
        acc[field.fieldName] = field.fieldValue || null;
        return acc;
      }, {});
      form.setFieldsValue(newValues);
    }

    setFinalUrl(buildFinalUrl(fields, channel));
  }, [fields, form]);

  function Header() {
    return (
      <div style={{ ...styles.hbxChannelForm.header }}>
        <h3>HBX Check-in Channel</h3>
        <h3>
          <strong>{channel.name}</strong>
        </h3>
        <div style={{ display: 'flex', justifyContent: 'right', marginBottom: 16 }}>
          <span style={{ marginLeft: 8, marginRight: 8, color: '#888' }}>
            {channel.active ? 'Active' : 'Inactive'}
          </span>
          <div
            style={{
              ...styles.hbxChannelForm.statusMark,
              backgroundColor: statusColor,
            }}
          />
        </div>
      </div>
    );
  }

  const renderField = field => {
    switch (field.fieldType) {
      case 'text':
        return <Input disabled={false} placeholder={field.placeholder || ''} />;
      case 'textArea':
        return (
          <Input.TextArea
            disabled={false}
            placeholder={field.placeholder || ''}
            rows={2}
            autoSize={{ minRows: 1, maxRows: 4 }}
          />
        );
      case 'select':
        return (
          <Select disabled={false} placeholder={field.placeholder || ''}>
            {(selectOptions || []).map((opt, idx) => (
              <Option key={idx} value={opt.value}>
                {opt.text}
              </Option>
            ))}
          </Select>
        );
      case 'boolean':
        return <Switch />;
      default:
        return <Input placeholder={field.placeholder || ''} />;
    }
  };

  const channelActivationHandler = async () => {
    try {
      const action = channel.active ? 'deactivate' : 'activate';
      const res = await API.changeChannelStatus(action, hotelId, channel.id);

      if (res.error) {
        return notification.warn({
          message: res.message,
        });
      }
      notification.success({
        message: res.message,
      });
      window.location.reload(false);
    } catch (error) {
      console.error('ERROR _> channelActivationHandler():', error);
      notification.error({
        message: 'Error saving channel activation',
      });
    }
  };

  const handleSave = async () => {
    try {
      const configs = [];
      const formValues = await form.validateFields();
      for (const key in formValues) {
        const element = formValues[key];
        configs.push({ fieldName: key, fieldValue: element });
      }

      const res = await API.saveChannelConfig(hotelId, channel.id, configs);

      if (res.error) {
        return notification.warn({
          message: res.message,
        });
      }

      notification.success({
        message: res.message,
      });
      window.location.reload(false);
    } catch (error) {
      console.error('ERROR _> handleSave():', error);
      notification.error({
        message: 'Error saving configuration data',
      });
    }
  };

  const statusColor = channel.active ? '#52c41a' : '#fa8c16';

  return (
    <div
      style={{
        ...styles.hbxChannelForm.box,
      }}
    >
      {/* Header */}
      <Header />

      {/* Dynamic form */}
      <Form
        form={form}
        layout="vertical"
        initialValues={fields?.reduce((acc, field) => {
          acc[field.fieldName] = field.fieldValue || null;
          return acc;
        }, {})}
      >
        {channel.baseUrl && (
          <Form.Item label="Base URL">
            <div
              style={{
                wordBreak: 'break-all',
                color: '#009ae9',
                padding: '4px 11px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
              }}
            >
              {channel.baseUrl}
            </div>
          </Form.Item>
        )}

        {fields.map(field => (
          <Form.Item
            key={field.id}
            name={field.fieldName}
            label={`${field.label} [ ${field.fieldName} ]`}
            rules={[{ required: field.required, message: `${field.label} field is required` }]}
          >
            {renderField(field)}
          </Form.Item>
        ))}

        {finalUrl && (
          <Form.Item label="Mapped channel URL">
            <Space.Compact style={{ width: '100%' }}>
              <div
                style={{
                  wordBreak: 'break-all',
                  color: channel.active ? '#52c41a' : '#fa8c16',
                  padding: '4px 11px',
                }}
              >
                {finalUrl}
              </div>
              <Button
                icon={<CopyOutlined />}
                onClick={() => {
                  if (finalUrl) {
                    navigator.clipboard.writeText(finalUrl);
                    message.success('URL copied to clipboard!');
                  } else {
                    message.warning('No URL to copy');
                  }
                }}
              />
            </Space.Compact>
          </Form.Item>
        )}

        <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" onClick={handleSave} style={{ marginRight: 8, borderRadius: 8 }}>
            {translate('salesChannels.hbxChannelForm.saveConfButton')}
          </Button>

          <Button danger onClick={channelActivationHandler} style={{ borderRadius: 8 }}>
            {channel.active
              ? translate('salesChannels.hbxChannelForm.deactivateChannelButton')
              : translate('salesChannels.hbxChannelForm.activateChannelButton')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}

/**
 * Construye la URL final a partir de un array de campos.
 *
 * @param {Array} fields - Lista de campos con { fieldName, fieldValue }
 * @param {Array} channel - Objeto channel {name, baseUrl}
 * @returns {string} finalUrl
 */
function buildFinalUrl(fields, channel) {
  const { code, baseUrl } = channel;

  switch (code) {
    case 'duve':
      return;

    case 'civitfun':
      return genericUrlBuilder(baseUrl, fields);

    case 'static-url':
      return;

    case 'parameterized-url':
      return;
  }
}

function genericUrlBuilder(baseUrl, fields) {
  fields
    .filter(f => f.fieldName !== 'base_url')
    .forEach(field => {
      if (!field.fieldValue) return;

      if (baseUrl.includes(field.fieldName)) {
        // :param || {queryParam}
        baseUrl = baseUrl
          .replace(`:${field.fieldName}`, field.fieldValue)
          .replace(`{${field.fieldName}}`, field.fieldValue);
      }
    });

  return baseUrl;
}
